// One example payload, told event by event — the data behind the payload
// events explorer (PayloadWebhookEventsExplorer), and the exact resources the
// "send a test event" action POSTs at a sandbox endpoint.
//
// The resources are the service's own published examples
// (services/curri-integrations-service/src/surfaces/rest/examples.ts) and the
// envelope is what its consumer builds around them (webhooks/consumer/render.ts),
// with the field order the partner mapper emits — and the test-send endpoint
// validates the body against `payloadSchema`, so drift here fails loudly rather
// than sending something the docs never showed.

const ACCOUNT_ID = 'acct_EXAMPLE001'

const PICKUP = {
  address: {
    addressLine1: '4341 W Van Buren St',
    city: 'Chicago',
    state: 'IL',
    postalCode: '60624',
  },
  contact: {
    company: 'Buenaventura Supply Co.',
    name: 'John Smith',
    phoneNumber: '+18055550100',
    emailAddress: 'jsmith@example.com',
  },
  timeWindow: {
    start: '2026-07-14T20:00:00.000Z',
    end: '2026-07-14T22:00:00.000Z',
  },
  notes: 'Drive to the front of the store',
}

const DROPOFF = {
  coordinates: { latitude: 41.8781, longitude: -87.6847 },
  contact: {
    name: 'Jane Smith',
    phoneNumber: '+18055550101',
    emailAddress: 'jane@example.com',
  },
  notes: 'This is in a new construction',
}

/** Imperial, because a response reports what Curri stored rather than what was sent. */
const ITEMS = [
  {
    description: 'Box of tile',
    lengthIn: 24,
    widthIn: 24,
    heightIn: 12,
    weightLbs: 43.9,
  },
]

const BOL_ONLY = { bolNumber: '2349' }
const WITH_PO = { poNumber: 'PO-99871', bolNumber: '2349' }

const CANCELED_STATUS = {
  code: 'failed',
  reasonCode: 'customer_canceled',
  note: 'Customer no longer needs this delivery',
}

type Location = { latitude: number; longitude: number; updatedAt: string | null }

type Attachment = { type: 'pickup' | 'dropoff' | 'signature'; url: string }

/** The driver Curri's marketplace assigned, as `data.fulfillment.driver` carries them. */
const driverAt = (location: Location | null) => ({
  id: 'driver_7KQ4M2XJ9D',
  firstName: 'Sam',
  lastName: 'Rivera',
  phoneNumber: '+18055550199',
  vehicle: {
    color: 'White',
    description: 'Ford Transit 250',
    licensePlate: '8ABC123',
  },
  location,
})

type Fulfillment = {
  driver: ReturnType<typeof driverAt> | null
  proofOfDelivery: {
    deliveredAt: string | null
    recipientName: string | null
    attachments: Attachment[]
  } | null
}

const PICKUP_PHOTO: Attachment = {
  type: 'pickup',
  url: 'https://files.curri.com/stop-task-images/example-pickup.jpg',
}
const DROPOFF_PHOTO: Attachment = {
  type: 'dropoff',
  url: 'https://files.curri.com/stop-task-images/example-dropoff.jpg',
}
const SIGNATURE: Attachment = {
  type: 'signature',
  url: 'https://files.curri.com/stop-task-images/example-signature.png',
}

/** Freight on the vehicle: the pickup photo is the only evidence so far. */
const PICKED_UP_PROOF: Fulfillment['proofOfDelivery'] = {
  deliveredAt: null,
  recipientName: null,
  attachments: [PICKUP_PHOTO],
}

const positionAt = (
  latitude: number,
  longitude: number,
  updatedAt: string,
  proofOfDelivery: Fulfillment['proofOfDelivery'] = null
): Fulfillment => ({
  driver: driverAt({ latitude, longitude, updatedAt }),
  proofOfDelivery,
})

/** Booked onto the marketplace; no driver has accepted it yet. */
const BOOKED: Fulfillment = { driver: null, proofOfDelivery: null }

const ASSIGNED = positionAt(41.8902, -87.7621, '2026-07-14T20:04:48.000Z')
const TO_PICKUP = positionAt(41.8846, -87.7512, '2026-07-14T20:09:15.000Z')
const AT_PICKUP = positionAt(41.8758, -87.7339, '2026-07-14T20:22:03.000Z')
const PICKED_UP = positionAt(41.8757, -87.7341, '2026-07-14T20:31:40.000Z', PICKED_UP_PROOF)
const IN_TRANSIT = positionAt(41.8774, -87.7105, '2026-07-14T20:41:52.000Z', PICKED_UP_PROOF)
const AT_DROPOFF = positionAt(41.878, -87.6849, '2026-07-14T20:52:10.000Z', PICKED_UP_PROOF)
const TO_RETURN = positionAt(41.8779, -87.7001, '2026-07-14T21:06:27.000Z', PICKED_UP_PROOF)
const AT_RETURN = positionAt(41.8758, -87.7339, '2026-07-14T21:19:44.000Z', PICKED_UP_PROOF)

/** Back at the origin: the work is over, so the position is no longer reported. */
const RETURNED: Fulfillment = { driver: driverAt(null), proofOfDelivery: PICKED_UP_PROOF }

const DELIVERED: Fulfillment = {
  // The work is over, so the driver's position is no longer reported.
  driver: driverAt(null),
  proofOfDelivery: {
    deliveredAt: '2026-07-14T20:58:31.000Z',
    recipientName: 'Jane Smith',
    attachments: [PICKUP_PHOTO, DROPOFF_PHOTO, SIGNATURE],
  },
}

interface EventMoment {
  /** One line under the event name in the picker. */
  description: string
  /** One sentence about what changed on the payload at this moment. */
  caption: string
  status: { code: string; reasonCode?: string; note?: string }
  referenceNumbers: Record<string, string>
  /** `data.fulfillment`: null until the payload is on a Curri marketplace delivery. */
  fulfillment: Fulfillment | null
}

const EVENTS = {
  'payload.created': {
    description: 'Curri recorded a new payload',
    caption:
      'A payload was created. Curri holds a record of the freight and nothing is acting on it, which is what `draft` says — it claims nothing about whether the payload is complete.',
    status: { code: 'draft' },
    referenceNumbers: BOL_ONLY,
    fulfillment: null,
  },
  'payload.updated': {
    description: 'A field was amended',
    caption:
      'An amendment landed — this one added a PO number. The whole resource travels, not the change, so apply it as a replacement rather than a patch.',
    status: { code: 'draft' },
    referenceNumbers: WITH_PO,
    fulfillment: null,
  },
  'payload.status.scheduled': {
    description: 'The payload reached scheduled',
    caption:
      'A status change, named by the status the payload reached — the type and `data.payload.status.code` always agree, so branch on either. Booked onto a Curri marketplace delivery, the payload now carries `data.fulfillment`, with every part `null` until a driver accepts it.',
    status: { code: 'scheduled' },
    referenceNumbers: BOL_ONLY,
    fulfillment: BOOKED,
  },
  'payload.status.assigned_to_driver': {
    description: 'A driver accepted the delivery',
    caption:
      '`data.fulfillment.driver` names the driver and their vehicle, and `driver.location` is their position when Curri built the event. The driver stays on every event until the delivery ends.',
    status: { code: 'assigned_to_driver' },
    referenceNumbers: BOL_ONLY,
    fulfillment: ASSIGNED,
  },
  'payload.status.pickup_in_transit': {
    description: 'The driver is on the way to the pickup',
    caption:
      'The same driver, with a newer `driver.location`. A position arrives only with an event, so read it as where the driver was when Curri built the event, not a live feed.',
    status: { code: 'pickup_in_transit' },
    referenceNumbers: BOL_ONLY,
    fulfillment: TO_PICKUP,
  },
  'payload.status.pickup_arrived': {
    description: 'The driver reached the pickup',
    caption: 'The driver is at the pickup address; `driver.location` is that position.',
    status: { code: 'pickup_arrived' },
    referenceNumbers: BOL_ONLY,
    fulfillment: AT_PICKUP,
  },
  'payload.status.picked_up': {
    description: 'The freight is on the vehicle',
    caption:
      'Picked up. `proofOfDelivery` appears with the pickup photo as its first `attachments` entry; `deliveredAt` and `recipientName` stay `null` until the drop-off.',
    status: { code: 'picked_up' },
    referenceNumbers: BOL_ONLY,
    fulfillment: PICKED_UP,
  },
  'payload.status.dropoff_in_transit': {
    description: 'A driver is carrying it to the drop-off',
    caption:
      'On a Curri marketplace delivery, `data.fulfillment` names the driver and their last reported position while the freight moves. It is `null` on a payload with no marketplace delivery.',
    status: { code: 'dropoff_in_transit' },
    referenceNumbers: BOL_ONLY,
    fulfillment: IN_TRANSIT,
  },
  'payload.status.dropoff_arrived': {
    description: 'The driver reached the drop-off',
    caption:
      'The driver is at the drop-off. Photos or a signature taken before the drop-off completes join `proofOfDelivery.attachments` on the next event, not on their own.',
    status: { code: 'dropoff_arrived' },
    referenceNumbers: BOL_ONLY,
    fulfillment: AT_DROPOFF,
  },
  'payload.status.delivered': {
    description: 'Terminal — the payload was delivered',
    caption:
      'Delivered. `proofOfDelivery.attachments` holds the pickup photo, the drop-off photo and the signature, each with its `type`, plus who signed — only this payload\'s, even when one stop drops several. `driver.location` is no longer reported.',
    status: { code: 'delivered' },
    referenceNumbers: BOL_ONLY,
    fulfillment: DELIVERED,
  },
  'payload.status.return_in_transit': {
    description: 'The driver is bringing it back',
    caption:
      'The drop-off did not happen and the freight is going back to its origin. The driver and `driver.location` are still reported while the return is underway.',
    status: { code: 'return_in_transit' },
    referenceNumbers: BOL_ONLY,
    fulfillment: TO_RETURN,
  },
  'payload.status.return_arrived': {
    description: 'The driver is back at the return location',
    caption: 'The driver reached the return location; `driver.location` is that position.',
    status: { code: 'return_arrived' },
    referenceNumbers: BOL_ONLY,
    fulfillment: AT_RETURN,
  },
  'payload.status.returned': {
    description: 'Terminal — the payload was returned',
    caption:
      'Terminal: the freight is back at its return location instead of delivered. The driver stays on the block and `driver.location` is `null`, because the work is over.',
    status: { code: 'returned' },
    referenceNumbers: BOL_ONLY,
    fulfillment: RETURNED,
  },
  'payload.status.holding': {
    description: 'Movement is paused',
    caption:
      'Movement is paused until it is resolved, for example while Curri waits on instructions. A Curri marketplace delivery does not reach `holding`, so `data.fulfillment` is `null` here.',
    status: { code: 'holding' },
    referenceNumbers: BOL_ONLY,
    fulfillment: null,
  },
  'payload.status.failed': {
    description: 'Terminal — the payload failed',
    caption:
      'Terminal: `failed` is the only negative status. `reasonCode` separates your own withdrawal (`customer_canceled`) from an operational failure, and `note` is the sentence attached to it. This payload was withdrawn before Curri booked it, so `data.fulfillment` is `null`.',
    status: CANCELED_STATUS,
    referenceNumbers: BOL_ONLY,
    fulfillment: null,
  },
  'payload.status.restated': {
    description: 'The reason changed, the status did not',
    caption:
      'The same `failed` status with a corrected note. The code has not moved, so a handler that acted on reaching `failed` must not act again.',
    status: {
      ...CANCELED_STATUS,
      note: 'Customer no longer needs this delivery — duplicate of SO-4471',
    },
    referenceNumbers: BOL_ONLY,
    fulfillment: null,
  },
} satisfies Record<string, EventMoment>

export type ExampleEvent = keyof typeof EVENTS

export const EXAMPLE_EVENTS = Object.keys(EVENTS) as ExampleEvent[]

export const momentFor = (event: ExampleEvent): EventMoment => EVENTS[event]

/**
 * The payload at an event, as the object the test-send endpoint accepts. Key
 * order matches the partner mapper's object literal
 * (surfaces/rest/payload-mapping.ts), so the example diffs cleanly against a
 * body a real event carried.
 */
export const examplePayloadFor = (
  event: ExampleEvent
): Record<string, unknown> => {
  const moment = EVENTS[event]
  return {
    id: 'payload_7KQ4M2XJ9D',
    status: moment.status,
    accountId: ACCOUNT_ID,
    customerData: { orderNumber: '234' },
    referenceNumbers: moment.referenceNumbers,
    tags: ['North Region', 'Rush'],
    pickup: PICKUP,
    dropoff: DROPOFF,
    items: ITEMS,
    createdAt: '2026-07-13T10:02:11.000Z',
  }
}

/** The event's `data.fulfillment`, as the test-send endpoint accepts it. */
export const exampleFulfillmentFor = (event: ExampleEvent): Fulfillment | null =>
  EVENTS[event].fulfillment

/** The id Curri minted for this event, and the dedup key on the wire. */
const EVENT_ID = 'd9a1c4f2-6b3e-4a7c-9f18-2e5b7c0d1a44'
const CREATED_AT = '2026-07-14T18:31:07.212Z'
const API_VERSION = '2026-07-15'

export const exampleEnvelopeFor = (
  event: ExampleEvent
): Record<string, unknown> => ({
  id: EVENT_ID,
  type: event,
  createdAt: CREATED_AT,
  apiVersion: API_VERSION,
  isSandbox: true,
  source: 'partner',
  data: {
    payload: examplePayloadFor(event),
    fulfillment: exampleFulfillmentFor(event),
  },
})

/**
 * The headers that envelope goes out with. The signature is a placeholder: it
 * is an HMAC over `<timestamp>.<body>` keyed with the reader's own endpoint
 * secret, so no value printed here would verify against anything they hold.
 */
export const exampleHeadersFor = (
  event: ExampleEvent
): Record<string, string> => ({
  'Content-Type': 'application/json; charset=utf-8',
  'User-Agent': 'Curri-Webhooks/1 (+https://integrations.curri.com/docs/webhooks)',
  'Curri-Webhook-Id': EVENT_ID,
  'Curri-Webhook-Timestamp': String(Math.floor(Date.parse(CREATED_AT) / 1000)),
  'Curri-Event-Type': event,
  'Curri-Version': API_VERSION,
  'Curri-Signature': 'v1=<hex>',
})

export const exampleEnvelopeJsonFor = (event: ExampleEvent): string =>
  JSON.stringify(exampleEnvelopeFor(event), null, 2)
